import React, { useState } from "react";
import { FiEdit } from "react-icons/fi";
import { IoDocumentLockOutline } from "react-icons/io5";
import DimensionModel from "../DimensionModel";
import { RiFileInfoLine } from "react-icons/ri";
import { FaRegEye } from "react-icons/fa";
import { formatINR, formatINRShort } from "../../../../utils/formatINR";

const ResalePlotUnitTable = ({ units = [], onEdit, onAdd, totalUnitRow = 0 }) => {
  const tableRows = Array.from({ length: totalUnitRow }, (_, index) => {
    const unit = units[index] || null;
    return {
      id: unit?.id || `placeholder-${index}`,
      unitNo: unit?.unitNo || null,
      sqft: unit?.sqft || null,
      ratePerSqft: unit?.ratePerSqft || null,
      registrationCharge: unit?.registrationCharge || null,
      northDimension: unit?.northDimension || null,
      southDimension: unit?.southDimension || null,
      eastDimension: unit?.eastDimension || null,
      westDimension: unit?.westDimension || null,
      roadFacingSide: unit?.roadFacingSide || { id: "", name: "" },
      preferredLocationCharge: unit?.preferredLocationCharge || null,
      statusName: unit?.unitStatus?.name || "Available",
      unitStatus: unit?.unitStatus || { id: "", name: "Available" },
      order: unit?.order || index + 1,
      isPlaceholder: !unit || unit.isArchived,
      vendorGuideline: unit?.vendorGuideline || null,
      governmentGuideline: unit?.governmentGuideline || null,
      roadFacing: unit?.roadFacing || null,
      notes: unit?.notes || null,
    };
  });

  const [selectedDimension, setSelectedDimension] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const getStatusStyles = (status) => {
    switch (status) {
      case "Available":
        return "border-[#399E79] border bg-green-50 text-green-800";
      case "Agreement":
        return "border-[#FFDF94] border bg-yellow-50 text-yellow-800";
      case "Booked":
        return "border-[#EE6C4D] border bg-red-50 text-red-800";
      default:
        return "border-[#2280FF] border bg-blue-50 text-blue-800";
    }
  };

  return (
    <div className="bg-white rounded-[10px] p-6 shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full ">
          <thead>
            <tr className="bg-gray-100  text-base text-center rounded-md">
              <th className="px-4 py-3 text-gray-700 font-medium">Plot No.</th>
              <th className="px-4 py-3 text-gray-700 font-medium">SQ. FT</th>
              <th className="px-4 py-3 text-gray-700 font-medium">
                Rate Per SQ.FT
              </th>
              <th className="px-4 py-3 text-gray-700 font-medium">
                Total Price
              </th>
              <th className="px-4 py-3 text-gray-700 font-medium">Dimension</th>
              <th className="px-4 py-3 text-gray-700 font-medium">Status</th>
              <th className="px-4 py-3 text-gray-700 font-medium">Action</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.length > 0 ? (
              tableRows.map((row) => {
                const totalPrice = row.sqft && row.ratePerSqft ? row.sqft * row.ratePerSqft : 0;
                
                return (
                <tr
                  key={row.id}
                  className="border-b text-center border-gray-200 hover:bg-gray-50"
                >
                  <td className="px-4 text-sm py-4">{row.unitNo || "N/A"}</td>
                  <td className="px-4 py-4 text-sm ">{row.sqft || "N/A"}</td>
                  <td className="px-4 py-4 text-sm">{row.ratePerSqft || "N/A"}</td>
                  <td className="px-4 py-4 ftext-sm cursor-pointer" title={totalPrice ? formatINR(totalPrice) : "0"}>
                    {totalPrice ? formatINRShort(totalPrice) : "N/A"}
                  </td>
                  <td
                    onClick={() => {
                      setSelectedDimension(row);
                      setIsOpen(true);
                    }}
                    className="px-4 py-4 text-sm"
                  >
                    <div className="flex cursor-pointer items-center justify-center">
                      <div className="relative">
                        <div className="flex flex-col items-center justify-center gap-1 p-2">
                          <div className="relative w-12 h-12 bg-gradient-to-br from-purple-100 via-purple-200 to-purple-300 rounded-md shadow-md flex items-center justify-center text-center">
                            <div className="absolute truncate w-12 -top-1 left-0 right-0 -translate-y-4 text-[10px] font-medium text-center">
                              N-{row.northDimension || "N/A"}
                            </div>
                            <div className="absolute truncate w-12 -bottom-1 left-0 right-0 translate-y-4 text-[10px] font-medium text-center">
                              S-{row.southDimension || "N/A"}
                            </div>
                            <div className="absolute truncate w-12 -left-3 top-1/2 transform -translate-x-full -translate-y-1/2 flex items-center justify-end text-[10px] font-medium text-left">
                              W-{row.westDimension || "N/A"}
                            </div>
                            <div className="absolute truncate w-12 -right-3 top-1/2 transform translate-x-full -translate-y-1/2 flex items-center text-[10px] font-medium text-right">
                              E-{row.eastDimension || "N/A"}
                            </div>
                            <div className="text-[10px] font-bold text-gray-800 leading-tight">
                              {row.sqft || "N/A"}
                              <br />
                              Sq.ft
                            </div>
                          </div>

                          <div className="bg-gray-700 text-white  truncate w-20 font-medium text-[10px] px-2 rounded shadow mt-4 whitespace-nowrap">
                            {row.roadFacing || "N/A"}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <span
                      className={`px-3 py-1 text-xs rounded-full ${getStatusStyles(
                        row.unitStatus.name
                      )}`}
                    >
                      {row.unitStatus?.name}
                    </span>
                  </td>
                  {row.unitStatus?.name === "Available" ||
                  !row?.unitStatus?.name ? (
                    <td className="px-4 py-4">
                      {row.isPlaceholder ? (
                        <button
                          onClick={() =>
                            onAdd({
                              order: row.order,
                              unitNo: "",
                              sqft: null,
                              ratePerSqft: null,
                              guidelineSqftPrice: null,
                              northDimension: null,
                              eastDimension: null,
                              westDimension: null,
                              southDimension: null,
                              roadFacingSide: { id: "", name: "" },
                              preferredLocationCharge: null,
                              createdBy: "",
                              unitStatus: { id: "", name: "" },
                              propertyId: "",
                              statusName: "Available",
                              registrationCharge: null,
                              notes: "",
                            })
                          }
                          className="p-1 px-2 bg-customGreen text-white rounded-md  transition-colors flex items-center justify-center"
                          aria-label="Add plot"
                        >
                          <span className="text-sm">Add</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => onEdit(row)}
                          className="p-2 text-customGreen hover:bg-blue-50 rounded-md transition-colors"
                          aria-label="Edit plot"
                        >
                          <FiEdit size={20} />
                        </button>
                      )}
                    </td>
                  ) : (
                    <td
                      onClick={() => onEdit(row)}
                      className="text-center hover:text-customGreen flex mt-5 justify-center  items-center  text-2xl  cursor-pointer"
                    >
                      <FaRegEye />
                    </td>
                  )}
                </tr>
              );})
            ) : (
              <tr className="text-center">
                <td colSpan={7} className="py-8 text-gray-500">
                  No plots found
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            {onAdd && (
              <tr>
                <td colSpan={7} className="px-4 py-4 text-center">
                  <button
                    onClick={() =>
                      onAdd({
                        order: tableRows.length + 1,
                        unitNo: "",
                        sqft: 0,
                        ratePerSqft: 0,
                        northDimension: 0,
                        eastDimension: 0,
                        westDimension: 0,
                        southDimension: 0,
                        roadFacingSide: { id: "", name: "" },
                        roadWidth: 0,
                        createdBy: "",
                        unitStatus: { id: "", name: "" },
                        propertyId: "",
                        registrationCharge: 0,
                        vendorGuideline: 0,
                        governmentGuideline: 0,
                        notes: "",
                      })
                    }
                    className="px-4 py-2 bg-customGreen text-white rounded-md transition-colors flex items-center mx-auto"
                  >
                    Add New Plot
                  </button>
                </td>
              </tr>
            )}
          </tfoot>
        </table>
      </div>
      <DimensionModel
        isOpen={isOpen}
        row={selectedDimension}
        isClose={() => setIsOpen(false)}
      />
    </div>
  );
};

export default ResalePlotUnitTable;